import { useState, useContext } from 'react';
import { Modal, Button, Spinner, Form, Row, Col } from 'react-bootstrap';
import { UserContext } from './UserContext';
import DonationAmount from './DonationAmount';
import icValidationError from './assets/img/ic-danger.svg';
import api from './api/axiosInstance';
import { validateEmail } from './utils/validation';

const DonateModal = ({
    show,
    onHide,
    currency = '€',
    amount,
    setAmount,
    loading,
    error,
    onPay,
}) => {
    const { user } = useContext(UserContext);
    const isGuest = !user;

    const [isPublic, setIsPublic] = useState(false);

    // ✅ Guest donor fields
    const [firstName, setFirstName] = useState('');
    const [familyName, setFamilyName] = useState('');
    const [email, setEmail] = useState('');

    const [localErrors, setLocalErrors] = useState({});
    const [emailInfo, setEmailInfo] = useState('');
    const [emailChecking, setEmailChecking] = useState(false);

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        clearTimeout(window.donorEmailTimeout);
        window.donorEmailTimeout = setTimeout(async () => {
            const v = String(value || '').trim();

            // reset
            setLocalErrors((prev) => ({ ...prev, email: '' }));
            setEmailInfo('');

            if (!v) return;

            // local format check
            if (!validateEmail(v)) {
                setLocalErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
                return;
            }

            // server check
            try {
                setEmailChecking(true);

                const res = await api.post('/api/auth/check-email', { email: v });

                // ✅ if exists => block (any role)
                if (res.data?.exists) {
                    setEmailInfo('This email already has an account. You can still donate, and we’ll link it to that email.'); // ✅
                    return; // ✅ stop further processing
                }
            } catch (err) {
                console.error('check-email failed:', err);
                // optional: don’t block donation if check fails
            } finally {
                setEmailChecking(false);
            }
        }, 800);
    }; 


    const handleDonateClick = () => {
        if (isGuest) {
            onPay({
                isPublic,
                donor: {
                    firstName,
                    familyName,
                    email,
                },
            });
        } else {
            onPay({ isPublic });
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="donate-modal"
            className="with-form"
        >
            <Form
                onSubmit={(e) => {
                    e.preventDefault(); // ✅ prevent page reload
                    handleDonateClick();
                }}
            >
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="w-100 text-center donate-title">
                        Make a donation
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-0">
                    <DonationAmount
                        currency={currency}
                        amount={amount}
                        setAmount={setAmount}
                        error={error}
                    />

                    {/* ✅ Guest fields */}
                    {isGuest && (
                        <div className="pt-4">

                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group controlId="donorFirstName">
                                        <Form.Label>First name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="First name"
                                            autoComplete="given-name"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group controlId="donorFamilyName">
                                        <Form.Label>Last name</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={familyName}
                                            onChange={(e) => setFamilyName(e.target.value)}
                                            placeholder="Family name"
                                            autoComplete="family-name"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group className="validated" controlId="donorEmail">
                                        <Form.Label>Email <span>*</span></Form.Label>

                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={email}
                                            onChange={handleEmailChange}
                                            isInvalid={!!localErrors.email}
                                            required
                                            placeholder="you@email.com"
                                            autoComplete="email"
                                        />

                                        {(localErrors.email) && (
                                            <div className="custom-error-icon-wrapper">
                                                <img
                                                    src={icValidationError}
                                                    alt={localErrors.email}
                                                    className="custom-error-icon"
                                                />
                                                <div className="custom-tooltip">
                                                    {localErrors.email}
                                                </div>
                                            </div>
                                        )}

                                        {!localErrors.email && emailChecking && (
                                            <div className="font-size-sm mt-2">Checking…</div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <p className="font-size-sm mt-3 mb-0">
                                We'll create a <strong>donatorUser</strong> account for you after the donation.
                            </p>
                        </div>
                    )}

                    <div className="visible-toggle d-flex align-items-center justify-content-start mt-5">
                        <div className="d-flex align-items-center gap-2">
                            <Form.Group controlId="publicSwitch">
                                <Form.Check
                                    type="switch"
                                    id="public-switch"
                                    checked={isPublic}
                                    onChange={(e) => setIsPublic(e.target.checked)}
                                    className="visible-toggle__switch"
                                />
                            </Form.Group>
                        </div>
                        <p className="visible-toggle__label m-0 font-size-md">
                            Make my donation public
                        </p>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-0 justify-content-center pb-4">
                    <Button
                        className="donation-cta"
                        type="submit"
                        disabled={loading || (isGuest && !!localErrors.email)}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Redirecting...
                            </>
                        ) : (
                            'Donate Now'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default DonateModal;
